import { Users, Briefcase, TrendingUp, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { IconChip, type IconChipTone } from '@/components/ui/icon-chip';
import { Skeleton } from '@/components/ui/skeleton';

export interface DashboardStats {
  totalCandidates: number;
  openJobs: number;
  activePipeline: number;
  hiredThisMonth: number;
}

interface StatsCardsProps {
  stats: DashboardStats | null;
  isLoading: boolean;
}

const statConfig: Array<{
  key: keyof DashboardStats;
  label: string;
  icon: typeof Users;
  tone: IconChipTone;
}> = [
  {
    key: 'totalCandidates',
    label: 'Total Candidates',
    icon: Users,
    tone: 'blue',
  },
  {
    key: 'openJobs',
    label: 'Open Jobs',
    icon: Briefcase,
    tone: 'green',
  },
  {
    key: 'activePipeline',
    label: 'Active Pipeline',
    icon: TrendingUp,
    tone: 'purple',
  },
  {
    key: 'hiredThisMonth',
    label: 'Hired This Month',
    icon: Trophy,
    tone: 'emerald',
  },
];

export function StatsCards({ stats, isLoading }: StatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statConfig.map((config) => (
          <Card key={config.key}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statConfig.map((config) => {
        const value = stats?.[config.key] ?? 0;

        return (
          <Card key={config.key} className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <IconChip icon={config.icon} tone={config.tone} size="lg" />
                <div>
                  <p className="text-sm text-text-secondary">{config.label}</p>
                  <p className="text-2xl font-bold text-text-primary">
                    {value.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
